import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { formatWorkExperienceDateRange } from 'components/Shared/WorkExperienceDisplay/WorkExperienceDisplay';
import styles from './UserBrowseWorkExperienceList.module.scss';

/** Max work experience rows shown on browse cards (Typesense payloads can be large). */
export const MAX_BROWSE_WORK_EXPERIENCE_RENDERED = 2;

const workExperienceItemShape = {
  job_title: PropTypes.string,
  company_name: PropTypes.string,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
  description: PropTypes.string,
};

/**
 * Typesense / API may send Unix seconds, ms, ISO strings, or empty for "current role".
 *
 * @param {unknown} raw
 * @returns {string} ISO string for {@link formatWorkExperienceDateRange}, or '' if absent / invalid.
 */
function normalizeBrowseWorkExperienceDate(raw) {
  if (raw == null || raw === '') {
    return '';
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const sec = raw > 1e12 ? raw / 1000 : raw;
    const m = moment.unix(sec);
    return m.isValid() ? m.toISOString() : '';
  }
  const s = String(raw).trim();
  if (!s) {
    return '';
  }
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    const sec = n > 1e12 ? n / 1000 : n;
    const m = moment.unix(sec);
    return m.isValid() ? m.toISOString() : '';
  }
  const parsed = moment(s);
  return parsed.isValid() ? parsed.toISOString() : '';
}

/**
 * Normalizes Typesense `workexperience` / `work_experience` for the browse card.
 *
 * Supports API shape `{ title, company, description, start_date, end_date }` (Unix timestamps
 * allowed) and legacy `{ job_title, company_name, ... }` string dates.
 *
 * @param {Record<string, unknown>|null|undefined} hit
 * @returns {Array<{ job_title: string; company_name: string; start_date: string; end_date: string; description: string; id?: string|number }>}
 */
export function browseWorkExperienceFromHit(hit) {
  if (!hit || typeof hit !== 'object') {
    return [];
  }
  const raw = hit.workexperience ?? hit.work_experience;
  if (!Array.isArray(raw)) {
    return [];
  }
  const list = raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const titleRaw =
        entry.title != null && String(entry.title).trim() !== ''
          ? String(entry.title).trim()
          : entry.job_title != null
          ? String(entry.job_title).trim()
          : '';
      const companyRaw =
        entry.company != null && String(entry.company).trim() !== ''
          ? String(entry.company).trim()
          : entry.company_name != null
          ? String(entry.company_name).trim()
          : '';
      const description =
        entry.description != null ? String(entry.description).trim() : '';
      const start_date = normalizeBrowseWorkExperienceDate(entry.start_date);
      const end_date = normalizeBrowseWorkExperienceDate(entry.end_date);
      const job_title = titleRaw;
      const company_name = companyRaw;
      if (!job_title && !company_name && !description) {
        return null;
      }
      const id = entry.id != null ? entry.id : undefined;
      return {
        job_title,
        company_name,
        start_date,
        end_date,
        description,
        ...(id != null ? { id } : {}),
      };
    })
    .filter(Boolean);

  return list.slice(0, MAX_BROWSE_WORK_EXPERIENCE_RENDERED);
}

function UserBrowseWorkExperienceCard({ item }) {
  const dateRange = formatWorkExperienceDateRange(item);
  const title = item.job_title || '';
  const company = item.company_name || '';
  const description = item.description || '';

  return (
    <article className={styles.card}>
      <div className={styles.topRow}>
        {title ? <h3 className={styles.jobTitle}>{title}</h3> : null}
        {dateRange ? (
          <span className={styles.dateRange}>{dateRange}</span>
        ) : null}
      </div>
      {company ? <div className={styles.company}>{company}</div> : null}
      {description ? <p className={styles.description}>{description}</p> : null}
    </article>
  );
}

UserBrowseWorkExperienceCard.propTypes = {
  item: PropTypes.shape(workExperienceItemShape).isRequired,
};

/**
 * Work experience block for browse cards (InstantSearch hit).
 *
 * @param {{ items: Array<Record<string, unknown>> }} props
 */
export function UserBrowseWorkExperienceList({ items }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return null;
  }

  return (
    <section className={styles.section} data-browse-work-experience="">
      <h2 className={styles.heading}>Work Experience</h2>
      <div className={styles.list}>
        {list.map((item, index) => (
          <UserBrowseWorkExperienceCard
            key={
              item.id != null
                ? String(item.id)
                : `we-${item.job_title || ''}-${
                    item.company_name || ''
                  }-${index}`
            }
            item={item}
          />
        ))}
      </div>
    </section>
  );
}

UserBrowseWorkExperienceList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(workExperienceItemShape)),
};

UserBrowseWorkExperienceList.defaultProps = {
  items: [],
};

export default UserBrowseWorkExperienceList;
